import type Phaser from 'phaser';

export const MOON_PALETTE = {
  outline: 0x34334b,
  shadow: 0x575777,
  base: 0x8583a5,
  mid: 0xa6a3bd,
  light: 0xcac6d2,
  crater: 0x484960,
  craterDeep: 0x303143,
  warm: 0xb4a89d,
} as const;

export type MoonCrater = {
  x: number;
  y: number;
  radius: number;
  depth: 'large' | 'medium' | 'small';
};

export const MOON_CRATERS: readonly MoonCrater[] = [
  { x: -9, y: -7, radius: 5, depth: 'large' },
  { x: 7, y: 8, radius: 5, depth: 'large' },
  { x: 10, y: -8, radius: 4, depth: 'large' },
  { x: -12, y: 8, radius: 3, depth: 'medium' },
  { x: 1, y: -13, radius: 3, depth: 'medium' },
  { x: 2, y: 3, radius: 3, depth: 'medium' },
  { x: 14, y: 1, radius: 2, depth: 'medium' },
  { x: -3, y: 13, radius: 2, depth: 'medium' },
  { x: -16, y: -1, radius: 1, depth: 'small' },
  { x: 5, y: -3, radius: 1, depth: 'small' },
  { x: 15, y: 11, radius: 1, depth: 'small' },
] as const;

export const drawPixelMoon = (graphics: Phaser.GameObjects.Graphics): void => {
  graphics.clear();
  for (let y = -20; y <= 20; y += 2) {
    const halfWidth = Math.floor(Math.sqrt(Math.max(0, 400 - y * y)) / 2) * 2;
    const rowColor = y < -8 ? MOON_PALETTE.light : y > 10 ? MOON_PALETTE.shadow : MOON_PALETTE.base;
    graphics.fillStyle(MOON_PALETTE.outline).fillRect(-halfWidth - 2, y - 1, halfWidth * 2 + 4, 3);
    graphics.fillStyle(rowColor).fillRect(-halfWidth, y, halfWidth * 2, 2);
    if (halfWidth > 8) {
      graphics.fillStyle(MOON_PALETTE.shadow).fillRect(8, y, halfWidth - 8, 2);
    }
  }
  graphics.fillStyle(MOON_PALETTE.mid).fillRect(-16, -14, 12, 6);
  graphics.fillStyle(MOON_PALETTE.warm).fillRect(-4, 7, 8, 5);
  graphics.fillStyle(MOON_PALETTE.shadow).fillRect(5, -16, 9, 5);
  graphics.fillStyle(MOON_PALETTE.shadow).fillRect(-15, 2, 7, 5);
  for (const crater of MOON_CRATERS) drawCrater(graphics, crater);
};

const drawCrater = (graphics: Phaser.GameObjects.Graphics, crater: MoonCrater): void => {
  if (crater.depth === 'small') {
    graphics.fillStyle(MOON_PALETTE.light).fillRect(crater.x - 1, crater.y - 1, 2, 1);
    graphics.fillStyle(MOON_PALETTE.craterDeep).fillRect(crater.x, crater.y, 2, 2);
    return;
  }
  const diameter = crater.radius * 2;
  graphics
    .fillStyle(MOON_PALETTE.light)
    .fillRect(crater.x - crater.radius, crater.y - crater.radius, diameter - 1, 2);
  graphics
    .fillStyle(MOON_PALETTE.crater)
    .fillRect(crater.x - crater.radius, crater.y - crater.radius + 2, diameter, diameter - 2);
  graphics
    .fillStyle(MOON_PALETTE.craterDeep)
    .fillRect(
      crater.x - crater.radius + 2,
      crater.y,
      Math.max(2, diameter - 3),
      Math.max(2, diameter - 2),
    );
  graphics
    .fillStyle(MOON_PALETTE.mid)
    .fillRect(crater.x - crater.radius, crater.y, 2, crater.radius);
};
